export interface HighlightSegment {
    text: string;
    isHighlighted: boolean;
    key: string;
}

export interface UseHighlightTextOptions {
    /** The text to search within */
    text: string;
    /** Search query or queries to highlight */
    query?: string | string[];
    /** Whether to perform case-sensitive matching. Default: false */
    caseSensitive?: boolean;
    /** Whether to highlight all matches or only the first match. Default: false (first match only) */
    allMatches?: boolean;
}

/**
 * Escapes special regex characters in a string
 * @param str - String to escape
 * @returns Escaped string safe for use in RegExp
 */
function escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Internal function to split text into segments based on query matches.
 * Returns array of segments with highlighting metadata.
 */
function getHighlightSegments(
    text: string,
    query: string | string[],
    caseSensitive = false,
    allMatches = false
): HighlightSegment[] {
    // Early returns for edge cases
    if (!text) {
        return [];
    }

    // Normalize query to array
    const queries = Array.isArray(query) ? query : [query];

    // Filter out empty queries
    const validQueries = queries.filter(q => q && q.trim() !== '');

    if (validQueries.length === 0) {
        return [{ text, isHighlighted: false, key: '0' }];
    }

    // Sort queries by length (longest first) to match longer patterns first
    // This ensures "project" matches before "pro" in overlapping cases
    const sortedQueries = [...validQueries].sort((a, b) => b.length - a.length);

    // Escape special regex characters and combine queries with OR
    const escapedQueries = sortedQueries.map(escapeRegex).join('|');
    const flags = caseSensitive ? 'g' : 'gi';
    const regex = new RegExp(escapedQueries, flags);

    const segments: HighlightSegment[] = [];
    let lastIndex = 0;
    let segmentIndex = 0;

    // When there are multiple queries, treat each as a separate search term and highlight all
    // When there's a single query, allMatches controls whether to highlight all occurrences
    const isMultipleQueries = Array.isArray(query) && validQueries.length > 1;
    const shouldHighlightAll = allMatches || isMultipleQueries;

    // Find matches (first match only or all matches based on flags)
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
        // Add non-highlighted text before this match
        if (match.index > lastIndex) {
            segments.push({
                text: text.slice(lastIndex, match.index),
                isHighlighted: false,
                key: String(segmentIndex++)
            });
        }

        // Add highlighted match
        segments.push({
            text: match[0],
            isHighlighted: true,
            key: String(segmentIndex++)
        });

        lastIndex = regex.lastIndex;

        // If only highlighting first match, break after first match
        if (!shouldHighlightAll) {
            break;
        }

        // Prevent infinite loops on zero-length matches
        if (match[0].length === 0) {
            regex.lastIndex++;
        }
    }

    // Add remaining non-highlighted text after last match
    if (lastIndex < text.length) {
        segments.push({
            text: text.slice(lastIndex),
            isHighlighted: false,
            key: String(segmentIndex++)
        });
    }

    // If no matches found, return original text as single segment
    if (segments.length === 0) {
        return [{ text, isHighlighted: false, key: '0' }];
    }

    return segments;
}

/**
 * React hook to highlight text matching a search query.
 * Returns an array of text segments with highlighting metadata.
 * Only the first match is highlighted by default. Set `allMatches: true` to highlight all occurrences.
 *
 * @example
 * ```tsx
 * // Highlight only first match (default)
 * const segments = useHighlightText({
 *   text: 'Project Management - Manage',
 *   query: 'manage',
 *   caseSensitive: false
 * });
 *
 * return (
 *   <>
 *     {segments.map(segment => (
 *       segment.isHighlighted ? (
 *         <mark key={segment.key}>{segment.text}</mark>
 *       ) : (
 *         <span key={segment.key}>{segment.text}</span>
 *       )
 *     ))}
 *   </>
 * );
 * ```
 *
 * @example
 * ```tsx
 * // Highlight all matches
 * const segments = useHighlightText({
 *   text: 'Project Management Dashboard',
 *   query: 'a',
 *   allMatches: true
 * });
 * ```
 *
 * @example
 * ```tsx
 * // Multiple queries
 * const segments = useHighlightText({
 *   text: 'Project Management Dashboard',
 *   query: ['project', 'dashboard'],
 *   caseSensitive: false
 * });
 * ```
 */
export function useHighlightText(
    options: UseHighlightTextOptions
): HighlightSegment[] {
    const { text, query, caseSensitive = false, allMatches = false } = options;

    // Handle empty query
    if (!query) {
        return [{ text, isHighlighted: false, key: '0' }];
    }

    // Handle empty array or array of empty strings
    if (Array.isArray(query)) {
        const hasValidQuery = query.some(q => q && q.trim() !== '');
        if (!hasValidQuery) {
            return [{ text, isHighlighted: false, key: '0' }];
        }
    } else if (query.trim() === '') {
        return [{ text, isHighlighted: false, key: '0' }];
    }

    return getHighlightSegments(text, query, caseSensitive, allMatches);
}
